import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLeadModals } from "@/components/LeadModals";
import { goToSection } from "@/lib/scroll";

const LINKS = [
  { id: "offer", label: "The Offer" },
  { id: "how-it-works", label: "How It Works" },
  { id: "pricing", label: "Pricing" },
  { id: "success", label: "Success Stories" },
  { id: "faq", label: "FAQ" },
];

export const Navbar = () => {
  const { openTrial } = useLeadModals();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const go = (id) => {
    setMobileOpen(false);
    goToSection(id, location.pathname, navigate);
  };

  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-hero-black/10 bg-white/95 backdrop-blur-md" data-testid="navbar">
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-3" data-testid="nav-logo-link" aria-label="RealEstateHero360 home">
          <img src="/logo-transparent.webp" alt="RealEstateHero360 superhero shield logo" className="h-11 w-auto" />
          <span className="font-display text-lg font-bold uppercase tracking-tight text-hero-black leading-none">
            RealEstate<span className="text-hero-blue">Hero</span><span className="text-hero-gold">360</span>
          </span>
        </Link>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-semibold text-hero-black/70 transition-colors hover:text-hero-blue"
            >
              {l.label}
            </button>
          ))}
          <Link to="/gallery" data-testid="nav-link-gallery" className="text-sm font-semibold text-hero-black/70 transition-colors hover:text-hero-blue">
            Gallery
          </Link>
          <a href="/blog/" data-testid="nav-link-blog" className="text-sm font-semibold text-hero-black/70 transition-colors hover:text-hero-blue">
            Blog
          </a>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={openTrial}
            data-testid="nav-cta-trial"
            className="hidden rounded-md bg-hero-gold px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wide text-hero-black shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg sm:block"
          >
            First Month 50% Off
          </button>
          <button
            className="lg:hidden text-hero-black"
            onClick={() => setMobileOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle navigation menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="overflow-hidden border-t border-hero-black/10 bg-white lg:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="space-y-1 px-4 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  onClick={() => go(l.id)}
                  data-testid={`nav-mobile-link-${l.id}`}
                  className="block w-full rounded-md px-3 py-3 text-left font-semibold text-hero-black hover:bg-hero-cream"
                >
                  {l.label}
                </button>
              ))}
              <Link
                to="/gallery"
                onClick={() => setMobileOpen(false)}
                data-testid="nav-mobile-link-gallery"
                className="block rounded-md px-3 py-3 font-semibold text-hero-black hover:bg-hero-cream"
              >
                Gallery
              </Link>
              <a
                href="/blog/"
                data-testid="nav-mobile-link-blog"
                className="block rounded-md px-3 py-3 font-semibold text-hero-black hover:bg-hero-cream"
              >
                Blog
              </a>
              <button
                onClick={() => { setMobileOpen(false); openTrial(); }}
                data-testid="nav-mobile-cta-trial"
                className="mt-2 w-full rounded-md bg-hero-gold px-5 py-3 font-display text-sm font-semibold uppercase tracking-wide text-hero-black"
              >
                First Month 50% Off
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
